import { ButtonGhost, ButtonPrimary, Colors, Icon, MultilineInput } from '@amelio/ui-library';
import { useRef, useState } from 'react';

import type { ClipboardEvent } from 'react';

import { COMMENT_REPLY_COUNTER_FROM } from '@/constants/review/CommentReply';
import { useAmelioTranslations } from '@/hooks/utils/useAmelioTranslations';
import { formatDecimal } from '@/utils/numberFormat';

interface ReplyInputProps {
    /** Resolves once the reply is sent; a rejection keeps the text, so nothing typed is lost. */
    onSubmit: (content: string) => void | Promise<void>;
    isSubmitting?: boolean;
    /** The words of the field; the results' own by default. */
    placeholder?: string;
    ariaLabel?: string;
    /** The name of the control that sends the reply; the results' own by default. */
    submitLabel?: string;
    /** The results send by the send icon alone; a labelled button says what it does where there is room. */
    submitAs?: 'icon' | 'button';
    rows?: number;
    /** Takes the focus as it appears: the button that opened it is gone. */
    autoFocus?: boolean;
    /** Given, the field takes no more characters and counts them as the reply nears the limit. */
    maxLength?: number;
    dataTestId?: string;
}

const LAYOUT_CLASSES = {
    icon: { row: 'flex items-center gap-5', field: 'border-base-400 w-full rounded-3xl' },
    button: { row: 'flex flex-col gap-3 sm:flex-row sm:items-end', field: undefined },
} as const;

/**
 * Enter starts a new line, as in any text the respondent writes: the reply is sent by its own control,
 * the send icon or a labelled button.
 */
export const ReplyInput = ({
    onSubmit,
    isSubmitting = false,
    placeholder,
    ariaLabel,
    submitLabel,
    submitAs = 'icon',
    rows = 1,
    autoFocus = false,
    maxLength,
    dataTestId = 'reply-input',
}: ReplyInputProps) => {
    const { t, i18n } = useAmelioTranslations();
    const [replyText, setReplyText] = useState('');
    // The field cuts a paste at its limit without a word: said here, or the end of the text is lost unseen.
    const [isPasteCut, setIsPasteCut] = useState(false);
    // Not redundant with `isSubmitting`: that guard only commits one render after the click.
    const isSubmittingRef = useRef(false);
    const fieldRef = useRef<HTMLDivElement>(null);

    const fieldPlaceholder = placeholder ?? t('questionResult.replyPlaceholder');
    const sendLabel = submitLabel ?? t('questionResult.sendReply');
    const layout = LAYOUT_CLASSES[submitAs];
    const cannotSend = !replyText.trim() || isSubmitting;
    const pasteCutMessage =
        isPasteCut && maxLength !== undefined
            ? t('answer.pastedTextTruncated', {
                  maxLength: formatDecimal(maxLength, i18n.language, { fractionDigits: 0 }),
              })
            : undefined;
    const isNearLimit =
        maxLength !== undefined && replyText.length >= maxLength * COMMENT_REPLY_COUNTER_FROM;

    const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
        if (maxLength === undefined || !(event.target instanceof HTMLTextAreaElement)) {
            return;
        }

        const { value, selectionStart, selectionEnd } = event.target;
        const room = maxLength - (value.length - (selectionEnd - selectionStart));
        // The field keeps a line break as one character, whatever the clipboard holds.
        const pasted = event.clipboardData.getData('text').replace(/\r\n?/g, '\n');
        setIsPasteCut(pasted.length > room);
    };

    const handleChange = (value: string) => {
        setReplyText(value);
        if (maxLength === undefined || value.length < maxLength) {
            setIsPasteCut(false);
        }
    };

    const handleReplySubmit = async () => {
        const trimmedText = replyText.trim();
        if (!trimmedText || isSubmitting || isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;

        try {
            await onSubmit(trimmedText);
            handleChange('');
            // The send control is disabled by the emptied field and would drop the focus on the page.
            fieldRef.current?.querySelector('textarea')?.focus();
        } catch {
            // The caller has already said why.
        } finally {
            isSubmittingRef.current = false;
        }
    };

    return (
        <div
            className={layout.row}
            data-testid={dataTestId}
        >
            <div
                ref={fieldRef}
                className={'min-w-0 flex-1 rounded-xl'}
                onPaste={handlePaste}
            >
                <MultilineInput
                    value={replyText}
                    onChange={handleChange}
                    rows={rows}
                    placeholder={fieldPlaceholder}
                    className={layout.field}
                    maxLength={maxLength}
                    showCharacterCount={isNearLimit}
                    error={Boolean(pasteCutMessage)}
                    errorMessage={pasteCutMessage}
                    ariaLabel={ariaLabel ?? fieldPlaceholder}
                    dataTestId={'comment-reply-input'}
                    autoFocus={autoFocus}
                    autoGrow
                />
            </div>

            {submitAs === 'button' ? (
                <ButtonPrimary
                    size={'sm'}
                    fitContent
                    leftIcon={<Icon name={'essential-send'} />}
                    loading={isSubmitting}
                    disabled={cannotSend}
                    onClick={handleReplySubmit}
                    dataTestId={`${dataTestId}-submit`}
                >
                    {sendLabel}
                </ButtonPrimary>
            ) : (
                <ButtonGhost
                    shape={'pill'}
                    className={'size-11 min-w-0 shrink-0 px-0'}
                    leftIcon={
                        <Icon
                            name={'essential-send'}
                            color={Colors.BASE_600}
                            size={'xl'}
                        />
                    }
                    loading={isSubmitting}
                    disabled={cannotSend}
                    onClick={handleReplySubmit}
                    ariaLabel={sendLabel}
                    dataTestId={`${dataTestId}-submit`}
                />
            )}
        </div>
    );
};
